import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Loader2, Clock, TrendingUp } from 'lucide-react';
import api from '@/api';

const CATEGORY_CONFIG = {
  finance: { label: 'MARKETS', color: '#3B82F6', bg: '#EFF6FF' },
  technology: { label: 'TECH', color: '#10B981', bg: '#ECFDF5' },
  ai: { label: 'AI', color: '#6B7280', bg: '#F3F4F6' },
  economy: { label: 'ECONOMY', color: '#F59E0B', bg: '#FFFBEB' },
  crypto: { label: 'CRYPTO', color: '#F97316', bg: '#FFF7ED' },
  science: { label: 'SCIENCE', color: '#8B5CF6', bg: '#F5F3FF' },
  world_news: { label: 'WORLD', color: '#EF4444', bg: '#FEF2F2' },
  internet_culture: { label: 'CULTURE', color: '#F43F5E', bg: '#FFF1F2' },
  politics: { label: 'POLITICS', color: '#DC2626', bg: '#FEF2F2' },
  entertainment: { label: 'CELEB', color: '#EC4899', bg: '#FDF2F8' },
  lifestyle: { label: 'STYLE', color: '#A855F7', bg: '#FAF5FF' },
  sports: { label: 'SPORTS', color: '#16A34A', bg: '#F0FDF4' },
};

function FanCard({ topic, index, total, onClick }) {
  const cat = CATEGORY_CONFIG[topic.category] || CATEGORY_CONFIG.world_news;
  const rotation = (index - Math.floor(total / 2)) * 4;
  const yOffset = Math.abs(index - Math.floor(total / 2)) * 6;

  return (
    <motion.div
      data-testid={`digest-card-${index}`}
      initial={{ opacity: 0, y: 40, rotate: 0 }}
      animate={{ opacity: 1, y: yOffset, rotate: rotation }}
      transition={{ delay: index * 0.08, type: 'spring', stiffness: 200, damping: 20 }}
      whileHover={{ y: yOffset - 12, scale: 1.03, rotate: 0, zIndex: 50 }}
      onClick={() => onClick(topic)}
      className="absolute w-[280px] cursor-pointer"
      style={{
        zIndex: total - index,
        transformOrigin: 'bottom center',
      }}
    >
      <div className="rounded-2xl border border-gray-100 shadow-md overflow-hidden bg-white">
        <div className="h-2" style={{ backgroundColor: cat.color }} />
        <div className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <span
              className="text-[9px] font-bold px-2 py-0.5 rounded-full"
              style={{ backgroundColor: cat.bg, color: cat.color }}
            >
              {cat.label}
            </span>
            {topic.has_explanation && (
              <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-green-50 text-green-600">READY</span>
            )}
          </div>
          <p className="text-sm font-bold text-gray-900 leading-snug line-clamp-3">{topic.title}</p>
          <div className="flex items-center gap-2 mt-2">
            <Clock size={10} className="text-gray-300" />
            <span className="text-[10px] text-gray-400">{topic.time_ago}</span>
            {topic.trend_score > 0 && (
              <>
                <TrendingUp size={10} className="text-gray-300 ml-auto" />
                <span className="text-[10px] text-gray-400">{topic.trend_score}</span>
              </>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function DailyDigest({ onTopicClick, onBack }) {
  const [topics, setTopics] = useState([]);
  const [date, setDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('fan'); // fan | list

  useEffect(() => {
    const load = async () => {
      try {
        const data = await api.getDailyDigest();
        setTopics(data.topics || []);
        setDate(data.date || '');
      } catch (e) {
        console.error('Failed to load daily digest:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div data-testid="daily-digest" className="min-h-screen pb-24"
      style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}>
      {/* Header */}
      <div className="px-5 mb-6">
        <button data-testid="digest-back" onClick={onBack}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors">
          <ArrowLeft size={16} /> Back
        </button>
        <h1 className="text-2xl font-black text-gray-900 mb-1">Today's Digest</h1>
        <p className="text-sm text-gray-400">{date} &middot; {topics.length} trending topics</p>

        {/* View toggle */}
        <div className="flex gap-2 mt-4">
          <button data-testid="view-fan" onClick={() => setViewMode('fan')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all
              ${viewMode === 'fan' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500'}`}>
            Fan View
          </button>
          <button data-testid="view-list" onClick={() => setViewMode('list')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all
              ${viewMode === 'list' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500'}`}>
            List View
          </button>
        </div>
      </div>

      {topics.length === 0 ? (
        <div className="text-center py-16 px-5">
          <p className="text-gray-400 text-sm">No trending topics today. Check back later!</p>
        </div>
      ) : viewMode === 'fan' ? (
        /* Fan layout */
        <div className="flex items-center justify-center" style={{ minHeight: '420px' }}>
          <div className="relative" style={{ width: '280px', height: '360px' }}>
            {topics.slice(0, 7).map((topic, i) => (
              <FanCard
                key={topic.id}
                topic={topic}
                index={i}
                total={Math.min(topics.length, 7)}
                onClick={onTopicClick}
              />
            ))}
          </div>
        </div>
      ) : (
        /* List layout */
        <div className="px-5 space-y-3">
          {topics.map((topic, i) => {
            const cat = CATEGORY_CONFIG[topic.category] || CATEGORY_CONFIG.world_news;
            return (
              <motion.div
                key={topic.id}
                data-testid={`digest-list-item-${i}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                onClick={() => onTopicClick(topic)}
                className="bg-white rounded-2xl border border-gray-100 p-4 cursor-pointer
                  hover:shadow-md hover:border-gray-200 transition-all active:scale-[0.98]"
              >
                <div className="flex items-start gap-3">
                  <div className="w-1 h-full min-h-[40px] rounded-full shrink-0" style={{ backgroundColor: cat.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[9px] font-bold px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: cat.bg, color: cat.color }}>
                        {cat.label}
                      </span>
                      {topic.has_explanation && (
                        <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-green-50 text-green-600">READY</span>
                      )}
                    </div>
                    <p className="text-sm font-bold text-gray-900 leading-snug">{topic.title}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <Clock size={10} className="text-gray-300" />
                      <span className="text-[10px] text-gray-400">{topic.time_ago}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
